import React from "react"
import {
    Box,
    Card,
    CardHeader,
    CardContent,
    Avatar,
    Typography,
    Chip,
    Divider,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Tooltip,
} from "@mui/material"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import ListAltIcon from "@mui/icons-material/ListAlt"
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt"
import moment from "moment"

const colorMap = {
    purple: "#a259d9",
    orange: "#f4a261",
    blue: "#2196f3",
    green: "#43a047",
    red: "#e63946",
    yellow: "#ffd600",
    teal: "#26c6da",
    pink: "#ec407a",
    brown: "#8d6e63",
    gray: "#bdbdbd",
    indigo: "#3f51b5",
    cyan: "#00bcd4",
    lime: "#cddc39",
    amber: "#ffb300",
    deepOrange: "#ff7043",
    deepPurple: "#7c43bd",
    lightBlue: "#29b6f6",
    lightGreen: "#8bc34a",
    gold: "#ffd700",
    silver: "#c0c0c0",
}

const toLabel = (v) => {
    if (v == null) return ""
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") return String(v)

    if (typeof v === "object") {
        // padrões comuns da sua API
        return String(
            v.value ??
            v.name ??
            v.label ??
            v.number ??
            v.email ??
            v.cpf_cnpj ??
            v.cpfCnpj ??
            v.phone ??
            v.pix_key_value ??
            v.pixKeyValue ??
            v.bank ??
            (v.id != null ? `#${v.id}` : "[obj]")
        )
    }

    try {
        return String(v)
    } catch {
        return ""
    }
}

function ActivityLog({ activities = [], header = true, sxCard = { mt: 2 } }) {
    const list = Array.isArray(activities) ? activities : []

    if (list.length === 0) return null

    return (
        <Card sx={sxCard}>
            {header && (
                <>
                    <CardHeader
                        avatar={
                            <Avatar sx={{ bgcolor: "primary.main" }}>
                                <ListAltIcon />
                            </Avatar>
                        }
                        title={
                            <Typography variant="h6" fontWeight={600}>
                                Logs de atividades
                            </Typography>
                        }
                    />
                    <Divider />
                </>
            )}

            <CardContent>
                <Table size="small">
                    <TableBody>
                        {list.map((log) => {
                            const userLabel = toLabel(log?.user)
                            const descLabel = toLabel(log?.description)
                            const fromLabel = toLabel(log?.from)
                            const toLabelText = toLabel(log?.to)

                            const hasFrom = Boolean(fromLabel)
                            const hasTo = Boolean(toLabelText)

                            return (
                                <TableRow key={log?.id ?? `${log?.time ?? ""}-${userLabel}-${fromLabel}-${toLabelText}`}>
                                    <TableCell align="center" sx={{ py: 2 }}>
                                        <Tooltip title={log?.time ? moment(log.time).format("DD/MM/YYYY [às] HH:mm:ss") : "-"}>
                                            <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                                <AccessTimeIcon fontSize="small" color="action" />
                                                <Typography variant="caption" color="text.secondary">
                                                    {log?.time ? moment(log.time).fromNow() : "-"}
                                                </Typography>
                                            </Box>
                                        </Tooltip>
                                    </TableCell>

                                    <TableCell sx={{ py: 2 }}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Avatar
                                                src={log?.avatar}
                                                alt={userLabel}
                                                sx={{ width: 28, height: 28 }}
                                            />
                                            <Typography variant="body2">{userLabel || "-"}</Typography>
                                        </Box>
                                    </TableCell>

                                    <TableCell sx={{ py: 2 }}>
                                        <Typography variant="body2">{descLabel || "-"}</Typography>
                                    </TableCell>

                                    <TableCell>
                                        <Box sx={{ py: 2, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                            {hasFrom && (
                                                <Chip
                                                    label={fromLabel}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: colorMap[log?.from_color] || colorMap.gray,
                                                        color: "#fff",
                                                        fontWeight: 500,
                                                    }}
                                                />
                                            )}

                                            {hasFrom && hasTo && <ArrowRightAltIcon fontSize="small" sx={{ mx: 0.5 }} />}

                                            {hasTo && (
                                                <Chip
                                                    label={toLabelText}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: colorMap[log?.to_color] || colorMap.gray,
                                                        color: "#fff",
                                                        fontWeight: 500,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    )
}

export default ActivityLog
